import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { AppShell } from '@/shared/layout/AppShell';
import { ProtectedRoute } from '@/shared/auth/ProtectedRoute';
import { Roles } from '@/shared/auth/roles';
import { env } from '@/env';
import { resolveDashboardV2Element } from '@/dashboardV2RouteGate';

/**
 * Route tree. Every module page is `React.lazy` imported so the initial
 * bundle contains only the shell + login page. Wave 2 modules drop new
 * chunks behind the same import() pattern.
 */

const LoginPage = lazy(() =>
  import('@/modules/auth/LoginPage').then((m) => ({ default: m.LoginPage })),
);
const CallbackPage = lazy(() =>
  import('@/modules/auth/CallbackPage').then((m) => ({ default: m.CallbackPage })),
);
const DashboardPage = lazy(() =>
  import('@/modules/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const FytdDashboardPage = lazy(() =>
  import('@/modules/dashboard/FytdDashboardPage').then((m) => ({
    default: m.FytdDashboardPage,
  })),
);
const ClientsPage = lazy(() =>
  import('@/modules/clients/ClientsPage').then((m) => ({ default: m.ClientsPage })),
);
const ContactsPage = lazy(() =>
  import('@/modules/contacts/ContactsPage').then((m) => ({ default: m.ContactsPage })),
);
const OpportunitiesPage = lazy(() =>
  import('@/modules/opportunities/OpportunitiesPage').then((m) => ({
    default: m.OpportunitiesPage,
  })),
);
const AdminPage = lazy(() =>
  import('@/modules/admin/AdminPage').then((m) => ({ default: m.AdminPage })),
);
const ReportsPage = lazy(() =>
  import('@/modules/reports/ReportsPage').then((m) => ({ default: m.ReportsPage })),
);
const ReportRunPage = lazy(() =>
  import('@/modules/reports/ReportRunPage').then((m) => ({ default: m.ReportRunPage })),
);
const ReportBuilderPage = lazy(() =>
  import('@/modules/reports/builder/ReportBuilderPage').then((m) => ({
    default: m.ReportBuilderPage,
  })),
);
const EqChatPage = lazy(() =>
  import('@/modules/eq/EqChatPage').then((m) => ({ default: m.EqChatPage })),
);
const UpdatesPage = lazy(() =>
  import('@/modules/updates/UpdatesPage').then((m) => ({ default: m.UpdatesPage })),
);
const ForecastPage = lazy(() =>
  import('@/modules/forecast').then((m) => ({ default: m.ForecastPage })),
);
const PartnersPage = lazy(() =>
  import('@/modules/partners/PartnersPage').then((m) => ({ default: m.PartnersPage })),
);
const ClientSuccessPage = lazy(() =>
  import('@/modules/client-success/ClientSuccessPage').then((m) => ({
    default: m.ClientSuccessPage,
  })),
);

/**
 * Leads (Wave 4 / QFLY-11). F8 ships the API hooks, the route plumbing,
 * and thin page scaffolds; F5 / F6 overwrite the page files with the
 * real list / detail views. Importing the page files directly (rather
 * than the module barrel) keeps lazy-chunking tight and lets F5 / F6
 * swap implementations without touching this file.
 */
const LeadsPage = lazy(() =>
  import('@/modules/leads/LeadsPage').then((m) => ({ default: m.LeadsPage })),
);

/**
 * Campaigns (Marketing / P1). List + create/edit dialogs only — no detail
 * route (attribution surfaces on the linked lead/opportunity, not a campaign
 * detail page). Writes are admin-gated on the BE; the list is visible to all
 * authenticated users, so the route itself is not role-gated.
 */
const CampaignsPage = lazy(() =>
  import('@/modules/campaigns/CampaignsPage').then((m) => ({
    default: m.CampaignsPage,
  })),
);
const LeadDetailPage = lazy(() =>
  import('@/modules/leads/LeadDetailPage').then((m) => ({
    default: m.LeadDetailPage,
  })),
);

/**
 * SPIFF registrations (Marketing / Partner SPIFF — P4-PR6). Queue + detail;
 * the detail page holds the state-gated workflow actions. Writes are gated on
 * the BE (create = CAP_RECORD_WRITE, verbs = admin); the routes are open to
 * authenticated users, matching Campaigns/Leads. Page files imported directly
 * (not the module barrel) to keep lazy-chunking tight.
 */
const SpiffRegistrationsPage = lazy(() =>
  import('@/modules/spiff/SpiffRegistrationsPage').then((m) => ({
    default: m.SpiffRegistrationsPage,
  })),
);
const SpiffRegistrationDetailPage = lazy(() =>
  import('@/modules/spiff/SpiffRegistrationDetailPage').then((m) => ({
    default: m.SpiffRegistrationDetailPage,
  })),
);
const SpiffPayoutsPage = lazy(() =>
  import('@/modules/spiff/SpiffPayoutsPage').then((m) => ({
    default: m.SpiffPayoutsPage,
  })),
);

const LazyFallback = (): JSX.Element => (
  <Box className="flex h-64 items-center justify-center">
    <CircularProgress aria-label="Loading page" />
  </Box>
);

/**
 * JSX <Route> tree — canonical source of truth consumed by both the
 * browser data router (in `appRouter.ts`) and the test-only MemoryRouter.
 */
export const AppRoutes = (): JSX.Element => (
  <Suspense fallback={<LazyFallback />}>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Cognito hosted-UI redirects here with ?code=...&state=...; the */}
      {/* page sits OUTSIDE ProtectedRoute because the user isn't */}
      {/* authenticated yet at this point. Both paths are configured in */}
      {/* the Cognito user-pool client. */}
      <Route path="/auth/callback" element={<CallbackPage />} />
      <Route path="/callback" element={<CallbackPage />} />
      <Route element={<ProtectedRoute />}>
        <Route element={<AppShell />}>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/dashboard/fytd" element={<FytdDashboardPage />} />
          {/* Dashboard V2 — env gate removed 2026-10-08 (see
              resolveDashboardV2Element's own doc comment); now an ordinary
              lazy-loaded route, live in every environment. */}
          <Route path="/dashboard/v2" element={resolveDashboardV2Element(env.appEnv)} />
          <Route path="/clients" element={<ClientsPage />} />
          <Route path="/contacts" element={<ContactsPage />} />
          <Route path="/campaigns" element={<CampaignsPage />} />
          <Route path="/leads" element={<LeadsPage />} />
          <Route path="/leads/:id" element={<LeadDetailPage />} />
          <Route path="/spiff" element={<SpiffRegistrationsPage />} />
          <Route path="/spiff/:id" element={<SpiffRegistrationDetailPage />} />
          <Route path="/partners" element={<PartnersPage />} />
          <Route path="/opportunities" element={<OpportunitiesPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/reports/:slug" element={<ReportRunPage />} />
          <Route path="/forecast" element={<ForecastPage />} />
          <Route path="/forecast/sales-pipeline" element={<ForecastPage />} />
          <Route path="/client-success" element={<ClientSuccessPage />} />
          <Route path="/eq" element={<EqChatPage />} />
          <Route path="/updates" element={<UpdatesPage />} />
          <Route element={<ProtectedRoute requiresRole={Roles.Admin} />}>
            <Route path="/admin" element={<AdminPage />} />
            {/* Report builder is admin-only (authoring); running saved reports */}
            {/* stays open under /reports/:slug. Static segment outranks :slug. */}
            <Route path="/reports/builder" element={<ReportBuilderPage />} />
            <Route path="/reports/builder/:id" element={<ReportBuilderPage />} />
            {/* SPIFF payout console — admin-only (the ledger carries amounts). */}
            <Route path="/spiff-payouts" element={<SpiffPayoutsPage />} />
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  </Suspense>
);
